import { useSnackbar } from 'notistack'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useMutation } from 'providers/tanstack-query/useMutation'
import { UPDATE_AD_UNIT_PRODUCT_ENABLED } from './api/mutation'

import type { SwitchVariables, UseSwitchAdUnitProduct, useSwitchAdUnitProductProps } from './types'
import type {
  UpdateAdUnitsForProductSwitchMutation,
  UpdateAdUnitsForProductSwitchMutationVariables,
} from 'gql/graphql'

export const useSwitchAdUnitProduct = ({
  setLoading,
}: useSwitchAdUnitProductProps): UseSwitchAdUnitProduct => {
  const { t } = useTranslation('features/adUnit')
  const { enqueueSnackbar } = useSnackbar()

  const { isPending, mutateAsync } = useMutation<
    UpdateAdUnitsForProductSwitchMutationVariables,
    UpdateAdUnitsForProductSwitchMutation
  >({
    mutation: UPDATE_AD_UNIT_PRODUCT_ENABLED,
    onCompleted: async (data: UpdateAdUnitsForProductSwitchMutation) => {
      const errors = data.updateAdUnits?.errors

      if (errors && errors.length > 0) {
        enqueueSnackbar(t('form.error.update'), { variant: 'error' })
      } else {
        enqueueSnackbar(t('form.success.update'), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['adUnit'],
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const mapper = ({
    id,
    product,
    value,
  }: SwitchVariables): UpdateAdUnitsForProductSwitchMutationVariables['input'] => ({
    adUnitsAttributes: [
      {
        [`${product}Enabled`]: value,
        id,
      },
    ],
  })

  const update = (variables: SwitchVariables): void => {
    mutateAsync({
      input: mapper(variables),
    })
  }

  return { update }
}
